import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Catches a render error and shows it.
 *
 * Without this, an unhandled error anywhere in the tree unmounts everything and
 * leaves a blank page with no indication of what happened. A consultant seeing
 * a black screen has nothing to report; a consultant seeing the message and the
 * component stack has everything.
 *
 * A class component because React has no hook equivalent for this.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null; stack: string | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null, stack: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error, stack: null };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Console as well as screen: the component stack is far more useful than
    // the message alone, and it is easier to copy from the console.
    console.error('Unhandled error in the application tree', error, info.componentStack);
    this.setState({ error, stack: info.componentStack ?? null });
  }

  override render() {
    const { error, stack } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        padding: '32px', maxWidth: '900px', margin: '0 auto', color: '#242424',
      }}>
        <h1 style={{ fontSize: '20px', margin: '0 0 8px' }}>Something in the interface failed</h1>
        <p style={{ color: '#616161', marginTop: 0 }}>
          The page stopped rendering. The detail below is also in the browser console.
        </p>

        <pre style={{
          background: '#FDF3F4', border: '1px solid #C50F1F', borderRadius: '6px',
          padding: '12px', overflowX: 'auto', fontSize: '13px', whiteSpace: 'pre-wrap',
        }}>
          {error.name}: {error.message}
        </pre>

        {stack && (
          <details>
            <summary style={{ cursor: 'pointer', fontSize: '13px', color: '#616161' }}>
              Component stack
            </summary>
            <pre style={{
              background: '#F5F5F5', borderRadius: '6px', padding: '12px',
              overflowX: 'auto', fontSize: '12px', whiteSpace: 'pre-wrap',
            }}>{stack}</pre>
          </details>
        )}

        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '16px', padding: '6px 14px', fontSize: '14px', cursor: 'pointer',
            borderRadius: '4px', border: '1px solid #d1d1d1', background: '#fff',
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
